import { useEffect, useMemo, useState } from 'react';

import Image from 'next/image';
import styled, { keyframes } from 'styled-components';

import { FALLBACK_IMAGE_URL } from '@constants/constants';
import { optimizeImage } from '@utils/imageOptimization';

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;

  & img {
    margin: 0;
    object-fit: cover;
    width: 100%;
  }
`;

// `keyframes`, not a nested `@keyframes`: nested names stay global in styled-components v5.
const placeholderSweep = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

const SkeletonLoader = styled.div<{ width?: number; height?: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${placeholderSweep} 1.5s infinite;
`;

interface Props {
  url?: string;
  alt?: string;
  fallBackImage?: string;
  width?: number;
  height?: number;
  useOriginalImage?: boolean;
  onClick?(): void;
  priority?: boolean;
  providerId?: number | null;
  placeholderUrl?: string | null;
}

const CloudinaryItemImage = ({
  url,
  alt,
  fallBackImage = FALLBACK_IMAGE_URL,
  width,
  height,
  useOriginalImage,
  onClick,
  priority = false,
  providerId,
  placeholderUrl,
}: Props) => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const imageUrl = useMemo(() => {
    if (!url) return '';
    if (useOriginalImage) return url;
    const result = optimizeImage(
      url,
      width ? width * 2 : undefined,
      height ? Math.round(height * 1.5) : undefined,
      {
        providerId,
        placeholderUrl,
      }
    );
    return result;
  }, [url, width, height, useOriginalImage, providerId, placeholderUrl]);

  const src = !error ? imageUrl : fallBackImage;
  const shouldFade = !priority;

  // Only reset error when the source changes. Resetting `loading` here leaves
  // listing images invisible: cached Next/Image fires onLoad before this effect,
  // then loading is set back to true and onLoad never runs again.
  useEffect(() => {
    setError(false);
  }, [imageUrl]);

  return (
    <ImageWrapper>
      {loading && !error && <SkeletonLoader width={width} height={height} />}
      <Image
        width={width}
        height={height}
        src={src}
        alt={alt ?? 'S&F'}
        onError={() => {
          setError(true);
          setLoading(false);
        }}
        onClick={onClick}
        onLoad={() => {
          setLoading(false);
        }}
        priority={priority}
        loading={priority ? 'eager' : 'lazy'}
        style={{
          opacity: shouldFade ? (loading ? 0 : 1) : 1,
          transition: shouldFade ? 'opacity 0.3s ease' : undefined,
        }}
      />
    </ImageWrapper>
  );
};

export default CloudinaryItemImage;
